import { formatDecimal } from './utils';

/**
 * Formatting helpers for the monitoring report dashboard.
 *
 * The report ratios (`ctr`, `viewability_rate`, etc.) are stored as fractions
 * in [0, 1] per the pipeline reference, so percentages multiply by 100.
 */

/** Compact integer formatting (e.g. 1.2M, 45.3K) for impression/click counts. */
export function formatCount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';

  const abs = Math.abs(value);
  if (abs >= 1_000_000_000) return `${formatDecimal(value / 1_000_000_000, 1)}B`;
  if (abs >= 1_000_000) return `${formatDecimal(value / 1_000_000, 1)}M`;
  if (abs >= 1_000) return `${formatDecimal(value / 1_000, 1)}K`;
  return formatDecimal(value, 0);
}

/** Full integer with thousands separators (for tables/tooltips). */
export function formatInt(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return formatDecimal(value, 0);
}

/** Ratio (0..1) → percentage string. */
export function formatPercent(value: number | null | undefined, decimals = 2): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '-';
  return `${formatDecimal(value * 100, decimals)}%`;
}

/** Short date label (e.g. 23/06) for axis ticks. */
export function formatShortDate(dateString: string): string {
  const date = new Date(dateString);
  if (Number.isNaN(date.getTime())) return dateString;
  return date.toLocaleDateString('es-ES', { day: '2-digit', month: '2-digit' });
}

/** Duration in ms → human label (e.g. 1.2s, 3m 4s). */
export function formatDuration(ms: number | null | undefined): string {
  if (ms === null || ms === undefined || Number.isNaN(ms)) return '-';
  if (ms < 1000) return `${ms}ms`;
  const totalSeconds = Math.round(ms / 1000);
  if (totalSeconds < 60) return `${totalSeconds}s`;
  const minutes = Math.floor(totalSeconds / 60);
  const seconds = totalSeconds % 60;
  return `${minutes}m ${seconds}s`;
}
